import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateUserProfile } from '../services/firebase/users';

// ── World constants (resolution-independent) ──────────────────────────────────
const MAX_PLAYER_X = 5.5;
const PLAYER_ACCEL = 6.0;
const PLAYER_MAX_SPEED = 3.8;
const PLAYER_FRICTION = 14.0;
const NOTES = ['♩', '♪', '♫', '♬'];
const NOTE_COLORS = ['#00e5ff', '#ff4081', '#69f0ae', '#ffea00'];
const SPAWN_HALF = 5.5;
const POOL_SIZE = 60;
const GRID_LANES = [-4, -3, -2, -1, 0, 1, 2, 3, 4];
const GRID_H_COUNT = 7;

// ── Difficulty — all rates are per-second ─────────────────────────────────────
const INITIAL_SPEED = 0.50;
const SPEED_ACCEL = 0.006;
const INITIAL_SPAWN_INTERVAL = 0.40;
const MIN_SPAWN_INTERVAL = 0.10;
const SPAWN_RATE_GROWTH = 0.004;

// ── Collision ─────────────────────────────────────────────────────────────────
// z is now linear — COLLISION_DZ is the z threshold (0=horizon, 1=player)
// Piano half-width ≈ 0.10 world units; note visual half ≈ 0.07 → combined ~0.17
const COLLISION_DZ   = 0.90;
const COLLISION_X_HALF = 0.15;

// ── Colours ───────────────────────────────────────────────────────────────────
const C_SKY = '#09091a';
const C_FLOOR = '#0c0c1e';
const C_GRID_V = 'rgba(70, 35, 170, 0.38)';
const C_GRID_H = 'rgba(70, 35, 170, 0.22)';
const C_HORIZON = 'rgba(110, 55, 210, 0.55)';
const C_PIANO_BODY = '#1e1840';
const C_PIANO_LID = '#0d0b22';
const C_PIANO_WHITE = '#f2ede0';
const C_PIANO_BLACK = '#141414';
const C_PIANO_ACCENT = 'rgba(140, 90, 255, 0.5)';
const C_HUD = 'rgba(255,255,255,0.88)';
const C_HUD_BEST = '#ffd700';

// ── Layout — derives all pixel positions from actual canvas size ───────────────
function lay(cw, ch) {
  const horizonY = Math.round(ch * 0.30);
  const playerY  = ch - Math.round(ch * 0.086);
  const cx       = cw / 2;
  const hs       = cw * 0.525;
  const pw       = Math.min(Math.round(cw * 0.105), 72);
  const ph       = Math.round(pw * 0.62);
  const noteBase = Math.round(ch * 0.018);
  const noteRange = Math.round(ch * 0.096);
  return { horizonY, playerY, cx, hs, pw, ph, noteBase, noteRange };
}

// ── Drawing ───────────────────────────────────────────────────────────────────
function drawBackground(ctx, cw, ch, horizonY) {
  ctx.fillStyle = C_SKY;
  ctx.fillRect(0, 0, cw, horizonY);
  ctx.fillStyle = C_FLOOR;
  ctx.fillRect(0, horizonY, cw, ch - horizonY);
  ctx.fillStyle = C_HORIZON;
  ctx.fillRect(0, horizonY - 1, cw, 2);
}

function drawGrid(ctx, gs, ch, l) {
  ctx.lineWidth = 1;
  // Vertical lane lines — converge at vanishing point
  ctx.strokeStyle = C_GRID_V;
  for (const wx of GRID_LANES) {
    const bx = l.cx + (wx - gs.playerX) * l.hs;
    ctx.beginPath();
    ctx.moveTo(l.cx, l.horizonY);
    ctx.lineTo(bx, ch);
    ctx.stroke();
  }
  // Horizontal scrolling lines — z is now used directly (linear → constant visual speed)
  ctx.strokeStyle = C_GRID_H;
  for (let i = 0; i < GRID_H_COUNT; i++) {
    const z = (gs.gridOffset + i / GRID_H_COUNT) % 1.0;
    if (z < 0.02) continue;
    const y  = l.horizonY + (l.playerY - l.horizonY) * z;
    const lx = l.cx + (-SPAWN_HALF - gs.playerX) * z * l.hs;
    const rx = l.cx + (SPAWN_HALF  - gs.playerX) * z * l.hs;
    ctx.beginPath();
    ctx.moveTo(lx, y);
    ctx.lineTo(rx, y);
    ctx.stroke();
  }
}

function drawObstacles(ctx, gs, l) {
  // Collect active notes, sort back-to-front (painter's algorithm)
  const active = [];
  for (let i = 0; i < gs.pool.length; i++) {
    if (gs.pool[i].active) active.push(gs.pool[i]);
  }
  active.sort((a, b) => a.z - b.z);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const o of active) {
    // Linear z → constant visual speed; perspective still visible through X convergence
    const sx = l.cx + (o.worldX - gs.playerX) * o.z * l.hs;
    const sy = l.horizonY + (l.playerY - l.horizonY) * o.z;
    const fs = Math.max(4, Math.round(l.noteBase + l.noteRange * o.z));
    ctx.font = `bold ${fs}px sans-serif`;
    ctx.fillStyle = NOTE_COLORS[o.noteIdx];
    ctx.fillText(NOTES[o.noteIdx], sx, sy);
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

function drawPiano(ctx, l) {
  const px = l.cx - l.pw / 2;
  const py = l.playerY - l.ph / 2;
  ctx.fillStyle = C_PIANO_BODY;
  ctx.fillRect(px, py, l.pw, l.ph);
  const lidH = Math.max(4, Math.round(l.ph * 0.31));
  ctx.fillStyle = C_PIANO_LID;
  ctx.fillRect(px, py, l.pw, lidH);
  ctx.fillStyle = C_PIANO_ACCENT;
  ctx.fillRect(px, py, l.pw, 1);
  const numW = 7;
  const wkW = (l.pw - 2) / numW;
  const keysTop = py + lidH + 1;
  const keysH = l.ph - lidH - 2;
  for (let i = 0; i < numW; i++) {
    ctx.fillStyle = C_PIANO_WHITE;
    ctx.fillRect(px + 1 + i * wkW, keysTop, wkW - 1, keysH);
  }
  const bkW = wkW * 0.58;
  const bkH = keysH * 0.60;
  for (const pos of [0, 1, 3, 4, 5]) {
    const bkX = px + 1 + (pos + 1) * wkW - bkW / 2;
    ctx.fillStyle = C_PIANO_BLACK;
    ctx.fillRect(bkX, keysTop, bkW, bkH);
  }
}

function drawHUD(ctx, gs, highScore) {
  ctx.font = 'bold 13px monospace';
  ctx.fillStyle = C_HUD;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(gs.elapsed.toFixed(1) + 's', 8, 8);
  if (highScore > 0) {
    ctx.font = '10px monospace';
    ctx.fillStyle = C_HUD_BEST;
    ctx.fillText('Best: ' + highScore.toFixed(1) + 's', 8, 25);
  }
  ctx.textBaseline = 'alphabetic';
}

function drawIdleScene(ctx, cw, ch) {
  const l = lay(cw, ch);
  drawBackground(ctx, cw, ch, l.horizonY);
  const fakeGs = { playerX: 0, gridOffset: 0, pool: [] };
  drawGrid(ctx, fakeGs, ch, l);
  fakeGs.pool = [
    { active: true, worldX: -2.5, z: 0.50, noteIdx: 0 },
    { active: true, worldX:  1.8, z: 0.68, noteIdx: 1 },
    { active: true, worldX: -0.4, z: 0.80, noteIdx: 2 },
    { active: true, worldX:  3.0, z: 0.38, noteIdx: 3 },
    { active: true, worldX: -1.5, z: 0.90, noteIdx: 0 },
    { active: true, worldX:  2.2, z: 0.25, noteIdx: 2 },
  ];
  drawObstacles(ctx, fakeGs, l);
  drawPiano(ctx, l);
}

function createPool() {
  return Array.from({ length: POOL_SIZE }, () => ({
    active: false, worldX: 0, z: 0, noteIdx: 0,
  }));
}

function spawnNote(gs, worldX) {
  for (let i = 0; i < POOL_SIZE; i++) {
    const o = gs.pool[i];
    if (!o.active) {
      o.active = true;
      o.worldX = worldX !== undefined ? worldX : (Math.random() * 2 - 1) * SPAWN_HALF;
      o.z = 0;
      o.noteIdx = Math.floor(Math.random() * NOTES.length);
      return;
    }
  }
}

// Spawn 1–3 notes per wave, ensuring minimum spacing between simultaneous notes
function spawnWave(gs) {
  const rand = Math.random();
  const count = rand < 0.65 ? 1 : rand < 0.95 ? 2 : 3;
  const usedX = [];
  for (let i = 0; i < count; i++) {
    let wx;
    let tries = 0;
    do {
      wx = (Math.random() * 2 - 1) * SPAWN_HALF;
      tries++;
    } while (tries < 8 && usedX.some(x => Math.abs(x - wx) < 2.0));
    usedX.push(wx);
    spawnNote(gs, wx);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function MiniGame({ userId, initialHighScore = 0, onHighScoreUpdate }) {
  const { t } = useTranslation();
  const canvasRef   = useRef(null);
  const wrapperRef  = useRef(null);
  const dimRef      = useRef({ cw: 400, ch: 220 });
  const phaseRef    = useRef('idle');
  const gsRef       = useRef(null);
  const rafRef      = useRef(null);
  const activeRef   = useRef(false);
  const keysRef     = useRef({ left: false, right: false });
  const lastTimeRef = useRef(0);
  const highScoreRef = useRef(initialHighScore);

  const [phase, setPhase]   = useState('idle');
  const [highScore, setHighScore] = useState(initialHighScore);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ── Responsive canvas — height capped at 30% of viewport to avoid scrolling ─
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas  = canvasRef.current;
    if (!wrapper || !canvas) return;

    function applySize() {
      const cw = wrapper.clientWidth;
      if (cw === 0) return;
      // Height: natural 0.55 ratio, but never more than 30% of viewport height
      const maxCh = Math.round(window.innerHeight * 0.30);
      const ch = Math.max(120, Math.min(Math.round(cw * 0.55), maxCh));
      canvas.width  = cw;
      canvas.height = ch;
      dimRef.current = { cw, ch };
      if (phaseRef.current !== 'playing') {
        drawIdleScene(canvas.getContext('2d'), cw, ch);
      }
    }

    const ro = new ResizeObserver(applySize);
    ro.observe(wrapper);
    window.addEventListener('resize', applySize);
    applySize();
    return () => { ro.disconnect(); window.removeEventListener('resize', applySize); };
  }, []);

  // ── Keyboard listeners ────────────────────────────────────────────────────
  useEffect(() => {
    function onDown(e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); keysRef.current.left  = true; }
      if (e.key === 'ArrowRight') { e.preventDefault(); keysRef.current.right = true; }
    }
    function onUp(e) {
      if (e.key === 'ArrowLeft')  keysRef.current.left  = false;
      if (e.key === 'ArrowRight') keysRef.current.right = false;
    }
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup',   onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup',   onUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      activeRef.current = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ── Game functions ────────────────────────────────────────────────────────
  function handleDeath(elapsed) {
    activeRef.current = false;
    cancelAnimationFrame(rafRef.current);

    const score   = parseFloat(elapsed.toFixed(1));
    const newBest = score > highScoreRef.current;

    if (newBest) {
      highScoreRef.current = score;
      setHighScore(score);
      if (userId) updateUserProfile(userId, { miniGameHighScore: score }).catch(() => {});
      if (onHighScoreUpdate) onHighScoreUpdate(score);
    }

    startGame();
  }

  function startGame() {
    activeRef.current = false;
    cancelAnimationFrame(rafRef.current);

    keysRef.current = { left: false, right: false };
    gsRef.current = {
      playerX: 0, playerVX: 0,
      speed: INITIAL_SPEED,
      spawnInterval: INITIAL_SPAWN_INTERVAL,
      // Randomise first spawn so notes don't all arrive on the same beat
      spawnTimer: INITIAL_SPAWN_INTERVAL * (0.4 + Math.random() * 0.6), // eslint-disable-line react-hooks/purity
      // Aimed note: guaranteed spawn on top of the player at least every 3 s
      aimTimer: 2.5,
      gridOffset: 0, elapsed: 0,
      pool: createPool(),
    };
    lastTimeRef.current = 0;
    activeRef.current   = true;
    phaseRef.current    = 'playing';
    setPhase('playing');

    function loop(ts) {
      if (!activeRef.current) return;
      const gs = gsRef.current;
      const { cw, ch } = dimRef.current;

      const dt = lastTimeRef.current === 0
        ? 0.016
        : Math.min((ts - lastTimeRef.current) / 1000, 0.10);
      lastTimeRef.current = ts;

      // Player physics
      const keys = keysRef.current;
      if (keys.left) {
        gs.playerVX = Math.max(gs.playerVX - PLAYER_ACCEL * dt, -PLAYER_MAX_SPEED);
      } else if (keys.right) {
        gs.playerVX = Math.min(gs.playerVX + PLAYER_ACCEL * dt,  PLAYER_MAX_SPEED);
      } else {
        if (gs.playerVX > 0) gs.playerVX = Math.max(0, gs.playerVX - PLAYER_FRICTION * dt);
        else if (gs.playerVX < 0) gs.playerVX = Math.min(0, gs.playerVX + PLAYER_FRICTION * dt);
      }
      gs.playerX = Math.max(-MAX_PLAYER_X, Math.min(MAX_PLAYER_X, gs.playerX + gs.playerVX * dt));

      // Difficulty ramp
      gs.elapsed += dt;
      gs.speed         = INITIAL_SPEED + SPEED_ACCEL * gs.elapsed;
      gs.spawnInterval = Math.max(MIN_SPAWN_INTERVAL, INITIAL_SPAWN_INTERVAL - SPAWN_RATE_GROWTH * gs.elapsed);
      gs.gridOffset    = (gs.gridOffset + gs.speed * dt) % 1.0;

      // Spawn — randomised interval so timing varies; each wave can be 1–3 notes
      gs.spawnTimer -= dt;
      if (gs.spawnTimer <= 0) {
        spawnWave(gs);
        // Next wave fires after 60–140% of the current base interval
        gs.spawnTimer = gs.spawnInterval * (0.6 + Math.random() * 0.8);
      }

      // Aimed spawn — guaranteed note on the player's current X every 3 seconds,
      // forcing the player to move rather than camping in one lane.
      gs.aimTimer -= dt;
      if (gs.aimTimer <= 0) {
        // Small jitter (±0.4) so the exact lane isn't perfectly telegraphed
        const jitter = (Math.random() - 0.5) * 0.8;
        spawnNote(gs, Math.max(-SPAWN_HALF, Math.min(SPAWN_HALF, gs.playerX + jitter)));
        gs.aimTimer = 1.0 + Math.random() * 4.0; // 1–5 s
      }

      // Move obstacles; collision check is now in linear z-space (no pow needed)
      let collision = false;
      for (let i = 0; i < POOL_SIZE; i++) {
        const o = gs.pool[i];
        if (!o.active) continue;
        o.z += gs.speed * dt;
        if (o.z > 1.04) { o.active = false; continue; }
        if (o.z >= COLLISION_DZ && Math.abs(o.worldX - gs.playerX) < COLLISION_X_HALF) {
          collision = true;
        }
      }

      // Draw
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const l   = lay(cw, ch);
      drawBackground(ctx, cw, ch, l.horizonY);
      drawGrid(ctx, gs, ch, l);
      drawObstacles(ctx, gs, l);
      drawPiano(ctx, l);
      drawHUD(ctx, gs, highScoreRef.current);

      if (collision) { handleDeath(gs.elapsed); return; }
      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
  }

  // Auto-start when the component first mounts (ADHD mode drawer opens)
  useEffect(() => {
    const timer = setTimeout(() => startGame(), 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center gap-2 select-none">

      {/* Canvas wrapper — fills full available width; height capped by viewport */}
      <div ref={wrapperRef} className="relative w-full">
        <canvas
          ref={canvasRef}
          width={400}
          height={220}
          className="w-full rounded-xl block"
          aria-label={t('miniGame.canvasLabel')}
        />

        {/* Idle overlay — briefly shown before auto-start fires */}
        {phase === 'idle' && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl"
            style={{ background: 'rgba(9,9,26,0.82)' }}
          >
            <div className="text-center space-y-1 px-4">
              <p className="font-bold text-lg" style={{ color: '#e0d8ff' }}>{t('miniGame.title')}</p>
              <p className="text-sm" style={{ color: '#9080c0' }}>{t('miniGame.subtitle')}</p>
            </div>
            {highScore > 0 && (
              <p className="text-sm font-mono" style={{ color: '#ffd700' }}>
                {t('miniGame.best')}: {highScore.toFixed(1)}s
              </p>
            )}
          </div>
        )}
      </div>

      {/* Mobile controls */}
      <div className="flex gap-3 w-full">
        <button
          aria-label={t('miniGame.moveLeft')}
          onPointerDown={() => { keysRef.current.left = true; }}
          onPointerUp={() => { keysRef.current.left = false; }}
          onPointerLeave={() => { keysRef.current.left = false; }}
          onContextMenu={(e) => e.preventDefault()}
          className="flex-1 py-3 rounded-xl font-bold text-2xl transition-opacity active:opacity-70 t-focus-ring touch-none"
          style={{ backgroundColor: 'rgba(91,33,182,0.35)', color: '#c4b5fd' }}
        >←</button>
        <button
          aria-label={t('miniGame.moveRight')}
          onPointerDown={() => { keysRef.current.right = true; }}
          onPointerUp={() => { keysRef.current.right = false; }}
          onPointerLeave={() => { keysRef.current.right = false; }}
          onContextMenu={(e) => e.preventDefault()}
          className="flex-1 py-3 rounded-xl font-bold text-2xl transition-opacity active:opacity-70 t-focus-ring touch-none"
          style={{ backgroundColor: 'rgba(91,33,182,0.35)', color: '#c4b5fd' }}
        >→</button>
      </div>

      <p className="text-xs t-text-disabled">{t('miniGame.controlsHint')}</p>
    </div>
  );
}
